// src/pages/LocationMasterPreview.js

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader, AlertCircle } from 'lucide-react';

const LocationMasterPreview = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [locations, setLocations] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  const API_URL = 'https://dpp-api.acespireconsulting.com/api/locations?limit=5&offset=0';

  const fetchLocations = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(API_URL, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) throw new Error(`Failed to fetch: ${response.status}`);

      const data = await response.json();

      if (data.success && data.data?.locations) {
        setLocations(data.data.locations);
        setTotalCount(data.data.pagination?.total || data.data.locations.length);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error('Error fetching locations:', err);
      setError(err.message || 'Failed to load location data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLocations();
  }, []);

  const handleViewTable = () => {
    navigate('/location-master');
  };



  // Loading
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Location Master</h3>
        <div className="flex items-center justify-center py-12">
          <Loader className="w-8 h-8 text-blue-600 animate-spin" />
        </div>
      </div>
    );
  }

  // Error
  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Location Master</h3>
        <div className="flex flex-col items-center justify-center py-12">
          <AlertCircle className="w-12 h-12 text-red-500 mb-3" />
          <p className="text-sm text-red-600 text-center">{error}</p>
          <button
            onClick={fetchLocations}
            className="mt-4 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Empty
  if (totalCount === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Location Master</h3>
          <button disabled className="px-4 py-2 text-blue-600 text-sm font-medium flex items-center gap-2 opacity-50 cursor-not-allowed">
            View Table
            <img src="/arrow.png" alt="" className="w-8 h-8" />
          </button>
        </div>
        <div className="text-center py-12">
          <svg className="w-16 h-16 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <p className="text-gray-500 text-sm">No location data available. Upload a CSV to get started.</p>
        </div>
      </div>
    );
  }

  // Data
  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-800">
          Location Master
          <span className="ml-2 text-sm font-normal text-gray-400">({totalCount} total)</span>
        </h3>
        <button
          onClick={handleViewTable}
          className="px-4 py-2 text-blue-600 rounded-lg text-sm font-medium hover:bg-blue-50 transition-colors flex items-center gap-2"
        >
          View Table
          <img src="/arrow.png" alt="" className="w-8 h-8" />
        </button>
      </div>

      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead style={{ backgroundColor: '#D6E1F9' }}>
            <tr>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Location ID</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Facility Name</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Type</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">City</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Country</th>
              <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Status</th>
            </tr>
          </thead>
          <tbody>
            {locations.map((loc, index) => (
              <tr key={loc.locationId || index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">
                  {loc.locationId || 'N/A'}
                </td>
                <td className="px-4 py-3 text-sm font-mono text-gray-600 max-w-[200px] truncate" title={loc.facilityName}>
                  {loc.facilityName || 'N/A'}
                </td>
                <td className="px-4 py-3 text-sm text-gray-700">{loc.locationType || 'N/A'}</td>
                <td className="px-4 py-3 text-sm text-gray-700">{loc.addressCity || '—'}</td>
                <td className="px-4 py-3 text-sm text-gray-700">{loc.addressCountry || '—'}</td>
                <td className="px-4 py-3 text-center">
                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                    loc.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
                  }`}>
                    {loc.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="lg:hidden p-4 space-y-3">
        {locations.map((loc, index) => (
          <div key={loc.locationId || index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <p className="text-xs text-gray-500 mb-1">Location ID</p>
                <p className="text-sm font-semibold text-gray-900 break-all">{loc.locationId || 'N/A'}</p>
              </div>
              <span className={`ml-2 flex-shrink-0 inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                loc.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
              }`}>
                {loc.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
            <div className="space-y-2">
              <div>
                <p className="text-xs text-gray-500">Facility Name</p>
                <p className="text-sm font-mono text-gray-700">{loc.facilityName || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Type</p>
                <p className="text-sm text-gray-700 mt-0.5">{loc.locationType || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Location</p>
                <p className="text-sm text-gray-700">
                  {[loc.addressCity, loc.addressCountry].filter(Boolean).join(', ') || '—'}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default LocationMasterPreview;